// ─── Role-Based Access Control ───────────────────────────
// Mirrors the backend role enum. This file is thin — the actual
// permission map lives in `frontend/src/config/rbac.js`. Sidebar
// visibility is DERIVED from that same config (NAV_ITEM_PERMISSIONS +
// hasPermission), so there is no separate role→items mapping to keep
// in sync. If a role has read on a resource, the corresponding sidebar
// item is visible.

import { hasPermission, NAV_ITEM_PERMISSIONS } from '../config/rbac';

export const ROLES = {
  systemAdmin:        'systemAdmin',
  orgAdmin:           'orgAdmin',
  claimsManager:      'claimsManager',
  seniorAdjuster:     'seniorAdjuster',
  adjuster:           'adjuster',
  fieldAdjuster:      'fieldAdjuster',
  complianceOfficer:  'complianceOfficer',
  externalContractor: 'externalContractor',
  claimantPortalUser: 'claimantPortalUser',
  policyholder:       'policyholder',
  broker:             'broker',
  riskCarrier:        'riskCarrier',
  underwriter:        'underwriter',
};

// Portal-user roles see the "PORTALS" section and only portalOnly nav
// items. Back-office roles see back-office items.
const PORTAL_ROLES = new Set(['policyholder', 'broker', 'claimantPortalUser']);

/** Is this role a portal user (external, limited access)? */
export function isPortalUser(role) {
  return PORTAL_ROLES.has(role);
}

// SaaS God (role === 'admin') is a platform-only actor with org=null.
// Every tenant-scoped nav item requires a tenant context, so surfacing
// Clients / Claims / Policies / etc. in the sidebar either 400s or
// returns empty — noise pretending SaaS God is "in" a tenant when they
// are not. Restrict the sidebar to the platform surface plus Account
// Settings; SaaS God inspects tenants by drilling into Tenant Management.
// Backend permissions are unchanged — hasPermission still returns true
// for admin, so URL-level access for debugging is preserved.
const ADMIN_VISIBLE_ITEMS = new Set(['platform', 'tenants', 'onboard-org', 'settings']);

// Regional deployments (e.g. ch.getregure.com) skip most /platform/*
// routes — they're gated behind REGURE_DEPLOYMENT_SCOPE=platform-admin
// in backend/router/v1.js. Surfacing dead nav items would just 404.
//
// BUT `/platform/tenants` is ALWAYS mounted (line 146 of that file —
// see `router.use('/platform/tenants', platformTenantsRouter)` OUTSIDE
// the scope gate), because per-tenant management is needed on every
// deploy so operator1/2 can toggle features (Reggie AI, etc.) on the
// regional tenants they host. Previously excluded from the sidebar
// which forced Ops to run direct-DB scripts for every toggle — the
// 2026-08-06 Reggie enable on ARGOS was one such incident.
// D-01, D-02, V-02, V-03, V-05.
const ADMIN_VISIBLE_ITEMS_REGIONAL = new Set(['tenants', 'settings']);

// Decide whether a single nav item is visible for a role, using ONLY
// the permission tuple declared in NAV_ITEM_PERMISSIONS. Fails closed
// when the item id has no entry. `deployScope` is the platform's
// REGURE_DEPLOYMENT_SCOPE (as fetched from /health) — 'platform-admin'
// or a regional value like 'regional-eu'. Defaults to platform-admin
// so admins on unknown deploys keep full sidebar (fail-open on scope
// unknown, fail-closed on regional).
function isItemVisible(role, itemId, deployScope) {
  const perm = NAV_ITEM_PERMISSIONS[itemId];
  if (!perm) return false;
  const portalUser = isPortalUser(role);

  // Portal / back-office segregation. Portal roles see only portalOnly
  // items; back-office roles see only non-portalOnly items.
  if (perm.portalOnly && !portalUser) return false;
  if (!perm.portalOnly && portalUser) return false;

  if (role === 'admin') {
    const scope = (deployScope || 'platform-admin').toLowerCase();
    const allowed = scope === 'platform-admin' ? ADMIN_VISIBLE_ITEMS : ADMIN_VISIBLE_ITEMS_REGIONAL;
    return allowed.has(itemId);
  }

  // Some items (e.g. my-profile) are always visible within their bucket.
  if (perm.always) return true;

  return hasPermission(role, perm.resource, perm.action);
}

/**
 * Filter the NAV_ITEMS array by the user's role. Derived from the RBAC
 * config — no per-role hardcoded list to maintain. `deployScope` is
 * optional; when omitted, defaults to platform-admin visibility.
 */
export function getVisibleNavItems(role, allSections, deployScope) {
  if (!role) return [];
  return allSections
    .map(s => ({ ...s, items: s.items.filter(i => isItemVisible(role, i.id, deployScope)) }))
    .filter(s => s.items.length > 0);
}

/**
 * Where a role lands after login: the first item of their own navigation.
 *
 * The login redirect used to carry its own table of per-role paths. It
 * drifted from the sidebar — a broker was sent to /portal/my-clients while
 * the first entry in their nav was /portal/my-policies — so the app opened
 * on a page the sidebar did not have highlighted, and a role whose nav
 * changed kept landing on the old page.
 *
 * Deriving it from the same list, filtered by the same allowlist, means the
 * two cannot disagree. Tenant feature flags are not applied here because
 * the tenant config has not loaded at redirect time; RequireFeature handles
 * a disabled landing module by redirecting onward.
 */
export function getLandingPath(role, allSections, deployScope, fallback = '/dashboard') {
  const sections = getVisibleNavItems(role, allSections, deployScope);
  for (const section of sections) {
    for (const item of section.items || []) {
      // `hidden` is a build-time flag and is known here, so a role whose
      // first permitted item is behind an unset LEGACY_ENABLE_* flag does
      // not land on a page missing from their own sidebar.
      if (item.path && item.hidden !== true) return item.path;
    }
  }
  return fallback;
}

// ─── Legacy convenience helpers (still used by a few screens) ───
// Each is a thin wrapper around hasPermission so behaviour stays in
// sync with the central config.

/** These roles can add/edit workflow rules */
export function canAddWorkflow(role) {
  return hasPermission(role, 'workflows', 'create');
}
/** These roles can edit risk signals config */
export function canEditRiskSignalConfig(role) {
  return hasPermission(role, 'riskSignals', 'write');
}
/** Can this role create a new claim? */
export function canCreateClaim(role) {
  return hasPermission(role, 'claims', 'create');
}
/** Can this role submit a new claim via the policyholder portal? */
export function canSubmitNewClaim(role) {
  return role === 'policyholder';
}
/** Can this role modify claims (status change / assign / edit)? */
export function canModifyClaim(role) {
  return hasPermission(role, 'claims', 'update');
}
/** Alias — status change uses the same gate as modify */
export function canChangeStatus(role) {
  return canModifyClaim(role);
}
/** Can this role access back-office claims section? */
export function canAccessBackofficeClaims(role) {
  return !isPortalUser(role) && hasPermission(role, 'claims', 'read');
}
/** Can this role view a specific tab on ClaimDetail? */
export function canViewTab(role, tabId) {
  if (tabId === 'financials') {
    return !['claimantPortalUser', 'policyholder', 'externalContractor'].includes(role);
  }
  if (tabId === 'audit') {
    return hasPermission(role, 'audit', 'read');
  }
  return true;
}
/** Should this role see internal notes? */
export function canSeeInternalNotes(role) {
  return !['claimantPortalUser', 'policyholder'].includes(role);
}
/** Is this role read-only on claims (no create/edit/status change)? */
export function isReadOnlyClaims(role) {
  return !hasPermission(role, 'claims', 'update');
}
/** Can this role view other brokers' data? */
export function canViewOtherBrokersData(role) {
  return hasPermission(role, 'brokers', 'read') && !isPortalUser(role);
}
